import React from 'react';

import { getAppointmentDoctorRoleGroups } from '../customer-services/customerServiceDoctorRoleHelpers';

/**
 * Hiển thị danh sách nhân sự phụ trách buổi hẹn từ record.services[]
 *
 * Props:
 * - record: appointment record (chứa services[])
 * - services: có thể truyền thẳng services[] thay vì record
 * - showRole: hiển thị tên role (Bác sĩ/Phụ tá...)
 * - dedupe: loại trùng theo id
 * - emptyText: text khi không có dữ liệu
 * - marginTop: khoảng cách giữa block doctors và assistants
 * - groupByService:
 *      true  = hiển thị/gom bác sĩ riêng trong từng service
 *      false = gộp dữ liệu của tất cả service rồi hiển thị
 * - collapseSingleDoctor:
 *      bật cơ chế gom các role có cùng tập bác sĩ
 *
 * Quy tắc gom:
 *
 * Khám       -> A, B
 * Điều trị   -> A, B
 * Cắm trụ    -> A, B
 * Phục hình  -> A, C
 *
 * Hiển thị:
 * Bác sĩ: A, B
 * Phục hình: A, C
 *
 * Nếu không có nhóm chiếm đa số rõ ràng:
 *
 * Khám       -> A, B
 * Điều trị   -> A, B
 * Cắm trụ    -> C, D
 * Phục hình  -> C, D
 *
 * => giữ nguyên từng role.
 */
const AppointmentStaffCell = ({
    record,
    services: servicesProp,
    showRole = true,
    dedupe = true,
    emptyText = '—',
    marginTop = 3,
    groupByService = false,
    collapseSingleDoctor = false,
}) => {
    const services = Array.isArray(servicesProp)
        ? servicesProp
        : Array.isArray(record?.services)
            ? record.services
            : [];

    /**
     * Lấy khóa nhận diện ổn định.
     *
     * Không dùng name để quyết định 2 người là cùng một người
     * vì có thể trùng tên.
     */
    const getPersonId = person => {
        const id = person?.id;

        if (id === null || id === undefined || id === '') {
            return null;
        }

        return String(id);
    };

    /**
     * Loại trùng để hiển thị.
     *
     * Với record không có id:
     * - không loại bỏ record
     * - tránh làm mất dữ liệu UI
     */
    const uniqById = arr => {
        if (!Array.isArray(arr)) {
            return [];
        }

        if (!dedupe) {
            return arr.filter(Boolean);
        }

        const map = new Map();
        const withoutId = [];

        arr.forEach(item => {
            if (!item) {
                return;
            }

            const id = getPersonId(item);

            if (!id) {
                withoutId.push(item);
                return;
            }

            if (!map.has(id)) {
                map.set(id, item);
            }
        });

        return [
            ...Array.from(map.values()),
            ...withoutId,
        ];
    };

    /**
     * Bảo vệ component trong trường hợp service có dữ liệu bất thường
     * hoặc helper phát sinh exception.
     *
     * Không để một service lỗi làm hỏng toàn bộ cell.
     */
    const safeGetDoctorRoleGroups = service => {
        try {
            const groups = getAppointmentDoctorRoleGroups(service);

            return Array.isArray(groups)
                ? groups.filter(Boolean)
                : [];
        } catch (error) {
            if (
                typeof process !== 'undefined' &&
                process.env?.NODE_ENV !== 'production'
            ) {
                // eslint-disable-next-line no-console
                console.warn(
                    'AppointmentStaffCell: không thể đọc danh sách bác sĩ',
                    error,
                    service
                );
            }

            return [];
        }
    };

    /**
     * Chuẩn hóa group.
     *
     * Không thay đổi cấu trúc nghiệp vụ,
     * chỉ đảm bảo render không lỗi khi dữ liệu thiếu.
     */
    const normalizeDoctorGroups = groups => {
        if (!Array.isArray(groups)) {
            return [];
        }

        return groups
            .filter(group => group && Array.isArray(group.people))
            .map((group, index) => ({
                ...group,

                key:
                    group.key ||
                    `doctor-role-${index}`,

                label:
                    group.label ||
                    'Bác sĩ',

                people: uniqById(
                    group.people
                        .filter(Boolean)
                        .map(person => ({
                            ...person,

                            name:
                                person.name ||
                                person.full_name ||
                                '—',
                        }))
                ),
            }))
            .filter(group => group.people.length > 0);
    };

    /**
     * Tạo signature từ TẬP bác sĩ của role.
     *
     * Ví dụ:
     *
     * [A, B] -> "1|2"
     * [B, A] -> "1|2"
     *
     * Thứ tự người không ảnh hưởng kết quả.
     *
     * Nếu có bác sĩ thiếu id -> return null.
     * Khi đó không collapse để tránh gom sai dữ liệu.
     */
    const getPeopleSignature = people => {
        if (!Array.isArray(people) || !people.length) {
            return null;
        }

        const ids = [];

        for (const person of people) {
            const id = getPersonId(person);

            if (!id) {
                return null;
            }

            ids.push(id);
        }

        const uniqueIds = Array.from(new Set(ids));

        uniqueIds.sort((a, b) =>
            a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base',
            })
        );

        return uniqueIds.join('|');
    };

    /**
     * Rút gọn role khác biệt.
     *
     * Bác sĩ khám      -> Khám
     * Bác sĩ điều trị  -> Điều trị
     * Bác sĩ cắm trụ   -> Cắm trụ
     * Bác sĩ phục hình -> Phục hình
     */
    const getCompactDoctorRoleLabel = label => {
        if (!label || typeof label !== 'string') {
            return 'Bác sĩ';
        }

        const compact = label
            .replace(/^Bác sĩ\s+/i, '')
            .replace(/^BS\s+/i, '')
            .trim();

        if (!compact) {
            return label;
        }

        return (
            compact.charAt(0).toUpperCase() +
            compact.slice(1)
        );
    };

    /**
     * Gom các role bác sĩ theo TẬP bác sĩ.
     *
     * Nguyên tắc:
     *
     * 1. Chỉ có 1 role
     *    => giữ nguyên role để không mất ý nghĩa.
     *
     * 2. Tất cả role có cùng tập bác sĩ
     *    => Bác sĩ: A, B
     *
     * 3. Có 1 tập bác sĩ xuất hiện nhiều nhất rõ ràng
     *    => Bác sĩ: nhóm chính
     *    => các role khác biệt hiển thị riêng.
     *
     * 4. Đồng hạng
     *    => giữ nguyên.
     *
     * 5. Thiếu id / dữ liệu không xác định
     *    => giữ nguyên.
     */
    const buildCollapsedDoctorGroups = inputGroups => {
        const groups = normalizeDoctorGroups(inputGroups);

        if (
            !collapseSingleDoctor ||
            groups.length < 2
        ) {
            return groups;
        }

        const groupsWithSignature = groups.map(group => ({
            group,
            signature: getPeopleSignature(group.people),
        }));

        /**
         * Có dữ liệu không đủ để xác định danh tính bác sĩ
         * => không gom.
         */
        if (
            groupsWithSignature.some(
                item => !item.signature
            )
        ) {
            return groups;
        }

        /**
         * Gom các role theo signature.
         */
        const signatureGroups = new Map();

        groupsWithSignature.forEach(item => {
            if (!signatureGroups.has(item.signature)) {
                signatureGroups.set(
                    item.signature,
                    {
                        signature: item.signature,
                        count: 0,
                        groups: [],
                    }
                );
            }

            const entry =
                signatureGroups.get(item.signature);

            entry.count += 1;
            entry.groups.push(item.group);
        });

        const candidates = Array.from(
            signatureGroups.values()
        ).sort((a, b) => b.count - a.count);

        if (!candidates.length) {
            return groups;
        }

        /**
         * Tất cả role đều có cùng tập bác sĩ.
         */
        if (candidates.length === 1) {
            const representative =
                candidates[0].groups[0];

            return [
                {
                    key: 'doctor',
                    label: 'Bác sĩ',
                    people: representative.people,
                },
            ];
        }

        const primary = candidates[0];
        const second = candidates[1];

        /**
         * Nhóm chính phải:
         *
         * - xuất hiện từ 2 role trở lên
         * - nhiều hơn nhóm đứng thứ hai
         *
         * Ví dụ:
         *
         * 3 - 1 => gom
         * 2 - 1 - 1 => gom
         * 2 - 2 => không gom
         * 1 - 1 - 1 => không gom
         */
        const hasClearPrimary =
            primary.count >= 2 &&
            primary.count > second.count;

        if (!hasClearPrimary) {
            return groups;
        }

        const representative =
            primary.groups[0];

        const result = [
            {
                key: 'doctor',
                label: 'Bác sĩ',
                people: representative.people,
            },
        ];

        /**
         * Giữ nguyên đầy đủ people của những role khác biệt.
         *
         * Ví dụ:
         *
         * Bác sĩ chung: A, B
         * Phục hình:    A, C
         *
         * Phải hiển thị:
         *
         * Bác sĩ: A, B
         * Phục hình: A, C
         *
         * Không được biến thành:
         *
         * Phục hình: C
         */
        groupsWithSignature.forEach(
            ({ group, signature }, index) => {
                if (signature === primary.signature) {
                    return;
                }

                result.push({
                    ...group,

                    key:
                        `different-${group.key}-${index}`,

                    label:
                        getCompactDoctorRoleLabel(
                            group.label
                        ),
                });
            }
        );

        return result;
    };

    const renderStaffList = items => (
        <div>
            {items.map((staff, index) => {
                const key =
                    getPersonId(staff) ||
                    `assistant-${index}`;

                return (
                    <div key={key}>
                        {showRole && (
                            <span
                                style={{
                                    color: '#999',
                                }}
                            >
                                {staff?.user_role?.name
                                    ? `${staff.user_role.name}: `
                                    : ''}
                            </span>
                        )}

                        <span
                            style={{
                                fontWeight: 600,
                            }}
                        >
                            {staff?.full_name ||
                                staff?.name ||
                                '—'}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    const renderDoctorGroups = groups => (
        <>
            {groups.map((group, index) => (
                <div
                    key={
                        group.key ||
                        `doctor-group-${index}`
                    }
                >
                    {showRole && (
                        <span
                            style={{
                                color: '#999',
                            }}
                        >
                            {group.label}:{' '}
                        </span>
                    )}

                    <span
                        style={{
                            fontWeight: 600,
                        }}
                    >
                        {group.people
                            .map(
                                person =>
                                    person?.name ||
                                    person?.full_name ||
                                    '—'
                            )
                            .join(', ')}
                    </span>
                </div>
            ))}
        </>
    );

    // ====================================================
    // OPTION A: HIỂN THỊ RIÊNG THEO TỪNG SERVICE
    // ====================================================

    if (groupByService) {
        const renderedServices = services
            .map((service, index) => {
                const doctorGroups =
                    buildCollapsedDoctorGroups(
                        safeGetDoctorRoleGroups(
                            service
                        )
                    );

                const assistants = uniqById(
                    Array.isArray(
                        service?.assistants
                    )
                        ? service.assistants
                        : []
                );

                if (
                    !doctorGroups.length &&
                    !assistants.length
                ) {
                    return null;
                }

                return {
                    service,
                    index,
                    doctorGroups,
                    assistants,
                };
            })
            .filter(Boolean);

        if (!renderedServices.length) {
            return (
                <span
                    style={{
                        color: '#bfbfbf',
                    }}
                >
                    {emptyText}
                </span>
            );
        }

        return (
            <div>
                {renderedServices.map(
                    (
                        {
                            service,
                            index,
                            doctorGroups,
                            assistants,
                        },
                        renderedIndex
                    ) => (
                        <div
                            key={
                                service?.id ||
                                `service-${index}`
                            }
                            style={{
                                marginBottom:
                                    renderedIndex ===
                                        renderedServices.length -
                                        1
                                        ? 0
                                        : 6,
                            }}
                        >
                            {doctorGroups.length >
                                0 &&
                                renderDoctorGroups(
                                    doctorGroups
                                )}

                            {assistants.length >
                                0 && (
                                    <div
                                        style={{
                                            marginTop:
                                                doctorGroups.length
                                                    ? marginTop
                                                    : 0,
                                        }}
                                    >
                                        {renderStaffList(
                                            assistants
                                        )}
                                    </div>
                                )}
                        </div>
                    )
                )}
            </div>
        );
    }

    // ====================================================
    // OPTION B: GỘP TẤT CẢ SERVICES
    // ====================================================

    const rolePeople = new Map();
    const assistantsAll = [];

    services.forEach(service => {
        const groups =
            safeGetDoctorRoleGroups(service);

        groups.forEach((group, index) => {
            if (
                !group ||
                !Array.isArray(group.people)
            ) {
                return;
            }

            const roleKey =
                group.key ||
                `doctor-role-${index}`;

            if (!rolePeople.has(roleKey)) {
                rolePeople.set(roleKey, {
                    ...group,
                    key: roleKey,
                    people: [],
                });
            }

            rolePeople
                .get(roleKey)
                .people.push(...group.people);
        });

        if (Array.isArray(service?.assistants)) {
            assistantsAll.push(
                ...service.assistants
            );
        }
    });

    /**
     * Gộp people của cùng role giữa nhiều services.
     *
     * Ví dụ:
     *
     * Service A:
     * Điều trị -> A
     *
     * Service B:
     * Điều trị -> B
     *
     * => Điều trị -> A, B
     */
    const doctorGroups =
        normalizeDoctorGroups(
            Array.from(rolePeople.values())
        );

    const displayedDoctorGroups =
        buildCollapsedDoctorGroups(
            doctorGroups
        );

    const assistants =
        uniqById(assistantsAll);

    if (
        !displayedDoctorGroups.length &&
        !assistants.length
    ) {
        return (
            <span
                style={{
                    color: '#bfbfbf',
                }}
            >
                {emptyText}
            </span>
        );
    }

    return (
        <>
            {displayedDoctorGroups.length > 0 &&
                renderDoctorGroups(
                    displayedDoctorGroups
                )}

            {assistants.length > 0 && (
                <div
                    style={{
                        marginTop:
                            displayedDoctorGroups.length
                                ? marginTop
                                : 0,
                    }}
                >
                    {renderStaffList(
                        assistants
                    )}
                </div>
            )}
        </>
    );
};

export default AppointmentStaffCell;